<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { ElMessage } from "element-plus";
import { Back, Check, CollectionTag, Delete, EditPen, FolderOpened, Message, Plus, Refresh, Search, Setting, Star, User } from "@element-plus/icons-vue";
import { api, jsonBody } from "../api";
import { formatMailAddresses, type MailAddress } from "../mail-address";
import { useSessionStore } from "../stores/session";
import ComposePanel from "./ComposePanel.vue";
import DomainSettings from "./DomainSettings.vue";

interface Overview { inbox: number; unread: number; sent: number; drafts: number }
interface MessageItem { id: string; direction: string; folder: string; sender: MailAddress[]; recipients: MailAddress[]; subject: string; preview: string; isRead: boolean; isStarred: boolean; isCatchAll: boolean; processingStatus: string; receivedAt: string }
interface MessageDetail extends MessageItem { text: string; html: string | null; cc: MailAddress[]; attachments: Array<{ id: string; filename: string; contentType: string; sizeBytes: number }> }
interface UserItem { id: string; address: string; loginEmail: string; displayName: string; role: string; status: string }

const session = useSessionStore();
const overview = ref<Overview>({ inbox: 0, unread: 0, sent: 0, drafts: 0 });
const messages = ref<MessageItem[]>([]);
const selected = ref<MessageDetail | null>(null);
const users = ref<UserItem[]>([]);
const folder = ref<"inbox" | "sent" | "trash" | "users" | "settings">("inbox");
const query = ref("");
const loading = ref(false);
const composing = ref(false);
const composeReply = ref<MessageDetail | null>(null);
const mobileDetail = ref(false);
const showRemote = ref(false);
const canAdmin = computed(() => session.bootstrap?.session.role === "domain_admin");
const currentAddress = computed(() => session.bootstrap?.session.email ?? "");

function senderLabel(sender: MessageItem["sender"]): string {
  const item = sender[0];
  if (typeof item === "string") return item;
  return item?.name || item?.address || "未知发件人";
}

async function load() {
  loading.value = true;
  try {
    overview.value = await api<Overview>("/api/v1/mail/overview");
    if (folder.value === "users") users.value = await api<UserItem[]>("/api/v1/admin/users");
    else if (folder.value !== "settings") messages.value = await api<MessageItem[]>(`/api/v1/mail/messages?folder=${folder.value}&q=${encodeURIComponent(query.value)}`);
  } catch (error) { ElMessage.error(error instanceof Error ? error.message : "加载失败"); }
  finally { loading.value = false; }
}

async function openMessage(item: MessageItem) {
  selected.value = await api<MessageDetail>(`/api/v1/mail/messages/${item.id}`);
  mobileDetail.value = true;
  item.isRead = true;
  if (overview.value.unread > 0) overview.value.unread -= 1;
}

async function toggleStar(item: MessageItem | MessageDetail) {
  item.isStarred = !item.isStarred;
  await api(`/api/v1/mail/messages/${item.id}`, { method: "PATCH", ...jsonBody({ isStarred: item.isStarred }) });
}

async function moveTrash(item: MessageDetail) {
  await api(`/api/v1/mail/messages/${item.id}`, { method: "PATCH", ...jsonBody({ folder: "trash" }) });
  selected.value = null; mobileDetail.value = false; await load();
}

function setFolder(value: typeof folder.value) { folder.value = value; selected.value = null; mobileDetail.value = false; }
function openCompose(reply: MessageDetail | null = null) { composeReply.value = reply; composing.value = true; }
watch(folder, () => { void load(); });
let searchTimer = 0;
watch(query, () => { window.clearTimeout(searchTimer); searchTimer = window.setTimeout(() => void load(), 260); });
onMounted(() => { void load(); });
</script>

<template>
  <div class="mail-layout">
    <aside class="mail-nav">
      <button class="compose-button" @click="openCompose()"><Plus /> 写邮件</button>
      <nav>
        <button :class="{ active: folder === 'inbox' }" @click="setFolder('inbox')"><Message /><span>收件箱</span><em>{{ overview.unread }}</em></button>
        <button :class="{ active: folder === 'sent' }" @click="setFolder('sent')"><Check /><span>已发送</span><em>{{ overview.sent }}</em></button>
        <button :class="{ active: folder === 'trash' }" @click="setFolder('trash')"><Delete /><span>回收站</span></button>
        <button v-if="canAdmin" :class="{ active: folder === 'users' }" @click="setFolder('users')"><User /><span>用户管理</span></button>
        <button v-if="canAdmin" :class="{ active: folder === 'settings' }" @click="setFolder('settings')"><Setting /><span>域名设置</span></button>
      </nav>
      <div class="mailbox-card"><span>当前邮箱</span><strong>{{ currentAddress }}</strong><small>{{ overview.inbox }} 封邮件 · {{ overview.drafts }} 份草稿</small></div>
    </aside>

    <DomainSettings v-if="folder === 'settings'" />

    <template v-else>
    <section class="message-list" :class="{ 'mobile-hidden': mobileDetail }" v-loading="loading">
      <div class="list-tools">
        <label v-if="folder !== 'users'" class="search-box"><Search /><input v-model="query" placeholder="搜索主题与正文摘要" /></label>
        <div v-else><span class="eyebrow">DOMAIN DIRECTORY</span><h2>用户目录</h2></div>
        <button class="icon-button" title="刷新" @click="load"><Refresh /></button>
      </div>
      <template v-if="folder !== 'users'">
        <button v-for="item in messages" :key="item.id" class="message-row" :class="{ unread: !item.isRead, selected: selected?.id === item.id }" @click="openMessage(item)">
          <span class="unread-mark" /><div class="message-meta"><strong>{{ senderLabel(item.sender) }}</strong><span v-if="item.isCatchAll" class="catch-all-label">无人收件</span><time>{{ new Date(item.receivedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) }}</time></div>
          <div class="message-subject">{{ item.subject || '（无主题）' }}</div><p>{{ item.processingStatus === 'queued' ? '邮件正在安全解析…' : item.preview }}</p>
          <span class="star-button" :class="{ active: item.isStarred }" @click.stop="toggleStar(item)"><Star /></span>
        </button>
        <div v-if="!messages.length" class="empty-state mail-empty"><FolderOpened /><p>{{ query ? '没有匹配的邮件' : '这里还很安静' }}</p><small>{{ query ? '尝试更短的搜索词' : '新邮件到达后会出现在这里' }}</small></div>
      </template>
      <template v-else>
        <div class="user-row" v-for="item in users" :key="item.id"><span class="user-avatar">{{ item.displayName[0]?.toUpperCase() }}</span><div><strong>{{ item.displayName }}</strong><small>{{ item.address }} · 登录 {{ item.loginEmail }}</small></div><span class="status-pill active">{{ item.role }}</span></div>
        <div v-if="!users.length" class="empty-state"><User /><p>尚无用户</p></div>
      </template>
    </section>

    <section class="reader" :class="{ 'mobile-visible': mobileDetail }">
      <div v-if="selected" class="reader-content">
        <div class="reader-tools"><button class="icon-button mobile-back" @click="mobileDetail = false"><Back /></button><span /><button class="icon-button" :class="{ starred: selected.isStarred }" title="星标" @click="toggleStar(selected)"><Star /></button><button class="icon-button" title="删除" @click="moveTrash(selected)"><Delete /></button></div>
        <span class="eyebrow">{{ selected.direction === 'inbound' ? (selected.isCatchAll ? 'CATCH-ALL LETTER · 无人收件' : 'RECEIVED LETTER') : 'SENT LETTER' }}</span>
        <h1>{{ selected.subject || '（无主题）' }}</h1>
        <div class="sender-block"><span class="user-avatar">{{ senderLabel(selected.sender)[0]?.toUpperCase() }}</span><div><strong>{{ senderLabel(selected.sender) }}</strong><small>发送至 {{ formatMailAddresses(selected.recipients) }}</small></div><time>{{ new Date(selected.receivedAt).toLocaleString() }}</time></div>
        <div v-if="selected.attachments.length" class="attachment-strip"><a v-for="attachment in selected.attachments" :key="attachment.id" :href="`/api/v1/mail/attachments/${attachment.id}`"><CollectionTag /><span>{{ attachment.filename }}</span><small>{{ Math.ceil(attachment.sizeBytes / 1024) }} KB</small></a></div>
        <div v-if="selected.html" class="html-notice"><span>远程图片已阻止</span><button class="text-button" @click="showRemote = !showRemote">{{ showRemote ? '保持阻止' : '仅本次显示' }}</button></div>
        <iframe v-if="selected.html" class="mail-html" sandbox="allow-popups" :srcdoc="selected.html" :class="{ 'show-remote': showRemote }" title="邮件正文" />
        <pre v-else class="mail-text">{{ selected.text }}</pre>
        <div class="reply-row"><button class="quiet-button" @click="openCompose(selected)"><EditPen /> 回复</button><button class="quiet-button" @click="openCompose(selected)">转发</button></div>
      </div>
      <div v-else class="reader-placeholder"><span class="reader-monogram">M</span><p>选择一封邮件开始阅读</p><small>邮件内容在隔离沙箱中呈现</small></div>
    </section>
    </template>
  </div>
  <ComposePanel v-if="composing" :from="currentAddress" :reply="composeReply" @close="composing = false" @sent="composing = false; load()" />
</template>
